import React, { Component } from 'react';
import classNames from 'classnames';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import Table from 'components/shared/table';
import Loadable from 'components/shared/loadable';
import InfoTooltip from 'components/shared/InfoTooltip';

const LOADING_VIEW = 'loading';
const LESSON_VIEW = 'lessons';
const PATH_VIEW = 'paths';

const SearchBox = ({ onSubmit, onChange, searchQuery }) => (
  <form className="IndexFilter IndexFilter-search" onSubmit={onSubmit}>
    <input
      className="NewInput NewInput--lightestGray"
      type="search"
      placeholder={t('common.search')}
      onChange={onChange}
      value={searchQuery}
    />
  </form>
);

const TabList = ({ children, className }) => (
  <div
    className={classNames('TabSection-tabsList', className)}
    aria-label={t('training_activity.content_button_label')}
  >
    {children}
  </div>
);

const Tab = ({ children, isActive, onSelect }) => (
  <button
    type="button"
    className={classNames('TabSection-tab u-resetBtn', {
      'TabSection-tab--active': isActive,
    })}
    tabIndex="0"
    onClick={onSelect}
  >
    {children}
  </button>
);

class AnalyzerRightsContents extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeView: LESSON_VIEW,
      lessonPage: 1,
      lessonPageCount: 0,
      lessonPerPage: 10,
      lessons: [],
      loading: true,
      pathPage: 1,
      pathPageCount: 0,
      pathPerPage: 10,
      paths: [],
      recentLessonSearchQuery: null,
      recentPathSearchQuery: null,
      searchQuery: '',
    };
  }

  componentDidMount() {
    this.getLessons();
  }

  goToView(view) {
    if (view === PATH_VIEW) {
      if (this.state.searchQuery !== this.state.recentPathSearchQuery) {
        this.getPaths();
      }
    } else if (view === LESSON_VIEW) {
      if (this.state.searchQuery !== this.state.recentLessonSearchQuery) {
        this.getLessons();
      }
    }

    this.setState({ activeView: view });
  }

  getActiveView() {
    return this.state.loading ? LOADING_VIEW : this.state.activeView;
  }

  isCurrentView(view) {
    return this.getActiveView() === view;
  }

  getLessons({
    page = this.state.lessonPage,
    perPage = this.state.lessonPerPage,
  } = {}) {
    const { searchQuery } = this.state;

    this.setState({ loading: true });

    request
      .get(routes.ANALYZER_RIGHTS.LESSONS, {
        query: searchQuery,
        perPage,
        page,
      })
      .then(data => {
        this.setState({
          lessons: data.lessons,
          loading: false,
          recentLessonSearchQuery: searchQuery,
          lessonPageCount: data.pageCount,
          lessonPage: data.page,
          lessonPerPage: data.perPage,
        });
      });
  }

  getPaths({
    page = this.state.pathPage,
    perPage = this.state.pathPerPage,
  } = {}) {
    const { searchQuery } = this.state;

    this.setState({ loading: true });

    request
      .get(routes.ANALYZER_RIGHTS.PATHS, {
        query: searchQuery,
        perPage,
        page,
      })
      .then(data => {
        this.setState({
          paths: data.paths,
          loading: false,
          recentPathSearchQuery: searchQuery,
          pathPageCount: data.pageCount,
          pathPage: data.page,
          pathPerPage: data.perPage,
        });
      });
  }

  handleLessonPageChange(page) {
    this.getLessons({ page });
  }

  handleLessonPerPageChange(perPage) {
    this.getLessons({ perPage });
  }

  handlePathPageChange(page) {
    this.getPaths({ page });
  }

  handlePathPerPageChange(perPage) {
    this.getPaths({ perPage });
  }

  handleSearchSubmit(event) {
    event.preventDefault();

    if (this.isCurrentView(LESSON_VIEW)) {
      this.getLessons();
    } else if (this.isCurrentView(PATH_VIEW)) {
      this.getPaths();
    }
  }

  showNoResultsRow() {
    return (
      <Table.Row>
        <Table.Body.Cell>{t('training_activity.no_results')}</Table.Body.Cell>
      </Table.Row>
    );
  }

  render() {
    const {
      lessons,
      paths,
      loading,
      searchQuery,
      lessonPageCount,
      lessonPage,
      lessonPerPage,
      pathPageCount,
      pathPage,
      pathPerPage,
    } = this.state;

    return (
      <div className="AnalyzerRightsContents DataCard fd-c p-24">
        <h3 className="DataCard-title">
          {t('training_activity.name')}
          <InfoTooltip
            tooltip={t('training_activity.description')}
            className="ml-4"
          />
        </h3>
        <Loadable loading={loading}>
          <div className="Index-toolbar">
            <TabList className="py-16">
              <Tab
                isActive={this.isCurrentView(LESSON_VIEW)}
                onSelect={() => {
                  this.goToView(LESSON_VIEW);
                }}
              >
                {t('activerecord.models.lesson.other')}
              </Tab>
              <Tab
                isActive={this.isCurrentView(PATH_VIEW)}
                onSelect={() => {
                  this.goToView(PATH_VIEW);
                }}
              >
                {t('activerecord.models.learning_paths/path.other')}
              </Tab>
            </TabList>
            <SearchBox
              searchQuery={searchQuery}
              onChange={event =>
                this.setState({ searchQuery: event.target.value })
              }
              onSubmit={event => this.handleSearchSubmit(event)}
            />
          </div>
          {this.isCurrentView(LOADING_VIEW) && <h3>{t('common.loading')}</h3>}
          {this.isCurrentView(LESSON_VIEW) && (
            <div>
              <Table alternate>
                <Table.Body>
                  {!lessons.length && this.showNoResultsRow()}
                  {lessons.map(lesson => (
                    <Table.Row key={lesson.id}>
                      <Table.Body.Cell>
                        <a tabIndex="0" href={lesson.urls.overview}>
                          {lesson.title}
                        </a>
                      </Table.Body.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Pagination
                    pageCount={lessonPageCount}
                    page={lessonPage}
                    perPage={lessonPerPage}
                    onPageChange={page => this.handleLessonPageChange(page)}
                    onPerPageChange={perPage =>
                      this.handleLessonPerPageChange(perPage)
                    }
                  />
                </Table.Footer>
              </Table>
            </div>
          )}
          {this.isCurrentView(PATH_VIEW) && (
            <div>
              <Table alternate>
                <Table.Body>
                  {!paths.length && this.showNoResultsRow()}
                  {paths.map(path => (
                    <Table.Row key={path.id}>
                      <Table.Body.Cell>
                        <a tabIndex="0" href={path.urls.overview}>
                          {path.title}
                        </a>
                      </Table.Body.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Pagination
                    pageCount={pathPageCount}
                    page={pathPage}
                    perPage={pathPerPage}
                    onPageChange={page => this.handlePathPageChange(page)}
                    onPerPageChange={perPage =>
                      this.handlePathPerPageChange(perPage)
                    }
                  />
                </Table.Footer>
              </Table>
            </div>
          )}
        </Loadable>
      </div>
    );
  }
}

export default AnalyzerRightsContents;
